/**
 * Measured camera and orbits for Skill's Galaxy V4, in 1920 × 1080 SVG units.
 * The original 31.2-second, 25 fps export contains almost two camera turns.
 * Reference: skill.com/wp-content/uploads/2025/07/Skill_DimensionalGraphics_Galaxy_V4_16x9-hevc-safari-1.mp4
 * Both the browser animation and generated static Twig artwork use this model.
 */
export const timing = {
  orbitSeconds: 15.625,
  loopSeconds: 31.2,
  frameRate: 25,
};

const camera = {
  perspectiveDepth: 0.871954,
  horizontalOffset: 78.04,
  orbitRadius: 332.468,
  verticalOffset: 45.85,
  heightScale: 0.9478,
  baseHeight: 11.733,
  heightVariation: 0.26,
  pixelCenterOffset: 0.5,
};

export const diamond = {
  width: 33,
  height: 40,
  path: 'M0.508249 21.4466L14.6639 39.1217C15.6022 40.2928 17.4036 40.2928 18.3419 39.1217L32.4918 21.4466C33.1694 20.597 33.1694 19.403 32.4918 18.5534L18.3361 0.878301C17.3978 -0.292767 15.5965 -0.292767 14.6581 0.878301L0.508249 18.5534C-0.169416 19.403 -0.169416 20.597 0.508249 21.4466Z',
};

/** @typedef {{ phaseRadians: number, centerX: number, centerY: number }} Orbit */

/** @type {Orbit[]} */
export const orbits = [
  { phaseRadians: -0.100529, centerX: 673.04372, centerY: 526.982076 },
  { phaseRadians: 0.448939, centerX: 671.119793, centerY: 508.371412 },
  { phaseRadians: -2.874909, centerX: 602.502171, centerY: 518.236873 },
  { phaseRadians: -2.68995, centerX: 612.480299, centerY: 422.801112 },
  { phaseRadians: -2.504991, centerX: 654.881827, centerY: 391.508585 },
  { phaseRadians: 1.188772, centerX: 629.234155, centerY: 377.597176 },
  { phaseRadians: -0.285488, centerX: 502.906369, centerY: 394.713306 },
  { phaseRadians: 2.483483, centerX: 675.517528, centerY: 362.993288 },
  { phaseRadians: 3.038359, centerX: 640.397041, centerY: 426.511893 },
  { phaseRadians: 1.373731, centerX: 637.634236, centerY: 365.810425 },
  { phaseRadians: -1.210281, centerX: 664.264005, centerY: 421.038389 },
  { phaseRadians: -1.025322, centerX: 681.785168, centerY: 421.762732 },
  { phaseRadians: 2.668441, centerX: 518.674024, centerY: 427.554678 },
  { phaseRadians: 1.558689, centerX: 564.54631, centerY: 405.029971 },
  { phaseRadians: 2.113565, centerX: 643.338381, centerY: 420.61119 },
  { phaseRadians: 1.743648, centerX: 576.502857, centerY: 440.198682 },
  { phaseRadians: -0.655406, centerX: 527.585261, centerY: 408.724011 },
  { phaseRadians: 2.8534, centerX: 524.033578, centerY: 522.10547 },
  { phaseRadians: 0.818856, centerX: 566.25952, centerY: 459.294428 },
  { phaseRadians: 1.928607, centerX: 538.343325, centerY: 498.630934 },
  { phaseRadians: -1.765157, centerX: 592.907874, centerY: 478.480455 },
  { phaseRadians: -0.840363, centerX: 559.202017, centerY: 535.779552 },
  { phaseRadians: 2.298525, centerX: 681.92969, centerY: 501.312461 },
  { phaseRadians: -3.059868, centerX: 527.468139, centerY: 498.579862 },
  { phaseRadians: -0.470447, centerX: 598.065742, centerY: 464.727917 },
  { phaseRadians: 1.003814, centerX: 630.015819, centerY: 407.187941 },
  { phaseRadians: -2.135067, centerX: 536.604484, centerY: 348.954476 },
  { phaseRadians: -1.395239, centerX: 645.620845, centerY: 446.921102 },
  { phaseRadians: -2.320033, centerX: 493.967672, centerY: 365.504688 },
  { phaseRadians: -1.950117, centerX: 652.198408, centerY: 389.251537 },
  { phaseRadians: -1.580198, centerX: 525.712756, centerY: 410.353889 },
  { phaseRadians: 0.633897, centerX: 564.139632, centerY: 402.681469 },
];

/**
 * Project one diamond onto the reference camera plane.
 * @param {Orbit} orbit
 * @param {number} seconds
 * @returns {string} SVG transform, also used to generate the no-JavaScript frame.
 */
export function projectOrbit({ phaseRadians, centerX, centerY }, seconds) {
  const angle = (seconds * Math.PI * 2) / timing.orbitSeconds - phaseRadians;
  const cosine = Math.cos(angle);
  const depth = 1 + camera.perspectiveDepth * cosine;
  const x = centerX + camera.pixelCenterOffset
    + (camera.horizontalOffset + camera.orbitRadius * Math.sin(angle)) / depth;
  const y = centerY + camera.pixelCenterOffset + camera.verticalOffset / depth;
  const height = camera.heightScale * (camera.baseHeight - camera.heightVariation * cosine) / depth;
  const left = x - height * diamond.width / (diamond.height * 2);
  const top = y - height / 2;
  return `translate(${left} ${top}) scale(${height / diamond.height})`;
}
